// src/components/Connections.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import { standardUserApi } from "../services/standardUserApi";
import useNoIndex from "../hooks/useNoIndex";
import Avatar from "./Avatar";
import LoadingState from "./LoadingState";
import QrCode from "./QrCode";
import { colors, type, radii, shadow, buttonPrimary, cardStyle, primaryHover } from "../styles/designTokens";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({ publicKey: currentKey });

const BENEFITS = [
  { icon: "🎉", title: "Plan hangouts in seconds", text: "Skip the group-chat back-and-forth — pick a time, drop a location, done." },
  { icon: "💬", title: "Never lose touch", text: "A gentle nudge before someone quietly drifts off your radar." },
  { icon: "🎁", title: "Trade ideas, not just plans", text: "Share wishlist ideas and see who else is already up for the same thing." },
];

const SHARE_TEXT = (url) => `Add me on OkGather so we can plan hangouts together: ${url}`;

const Connections = () => {
  const navigate = useNavigate();

  // This is a subscriber-only page, same as My Events/Edit Profile -
  // never indexed, and gated behind an active/trialing/cancelled plan.
  useNoIndex();

  const [isVerifyingAuth, setIsVerifyingAuth] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [connections, setConnections] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState(null);

  const [connectLink, setConnectLink] = useState("");
  const [linkLoading, setLinkLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    const validateAndLoad = async () => {
      const token = localStorage.getItem("memberstackToken");
      if (!token) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      let currentMember = null;
      try {
        currentMember = await memberstack.getCurrentMember();
      } catch (err) {
        console.error("Connections › getCurrentMember error:", err);
      }

      if (!currentMember || !currentMember.data) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      const hasValidStatus = (currentMember.data.planConnections || []).some((plan) => {
        const status = plan.status?.toLowerCase();
        const type = plan.type?.toLowerCase();
        return type === "subscription" && ["active", "trialing", "cancelled", "canceled"].includes(status);
      });

      setIsVerifyingAuth(false);

      if (!hasValidStatus) {
        navigate("/checkout");
        return;
      }

      try {
        const data = await standardUserApi.getConnections(token);
        setConnections(data);
      } catch (err) {
        console.error("Connections › load error:", err);
        setErrorMessage("Couldn't load your connections right now. Please try again shortly.");
      } finally {
        setLoading(false);
      }

      try {
        const linkData = await standardUserApi.getMyConnectionLink(token);
        setConnectLink(linkData.url);
      } catch (err) {
        console.error("Connections › load connect link error:", err);
      } finally {
        setLinkLoading(false);
      }
    };

    validateAndLoad();
  }, [navigate]);

  const handleAccept = async (connectionId) => {
    const token = localStorage.getItem("memberstackToken");
    setAcceptingId(connectionId);
    try {
      await standardUserApi.acceptConnection(token, connectionId);
      setConnections((prev) =>
        prev.map((c) => (c.id === connectionId ? { ...c, status: "accepted" } : c))
      );
    } catch (err) {
      console.error("Connections › accept error:", err);
      setErrorMessage("Couldn't accept that connection right now. Please try again shortly.");
    } finally {
      setAcceptingId(null);
    }
  };

  const handleCopyLink = () => {
    if (!connectLink) return;
    navigator.clipboard.writeText(connectLink).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2500);
    });
  };

  const shareVia = (method) => {
    if (!connectLink) return;
    const text = SHARE_TEXT(connectLink);
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text)}`,
      sms: `sms:?&body=${encodeURIComponent(text)}`,
      email: `mailto:?subject=${encodeURIComponent("Let's connect on OkGather")}&body=${encodeURIComponent(text)}`,
    };
    if (urls[method]) window.open(urls[method], "_blank");
  };

  // On a phone standing next to someone (e.g. right after a gathering),
  // this opens the OS share sheet - AirDrop, Nearby Share, Messages, etc -
  // so the link reaches their phone in a couple of taps with nothing to
  // type or read aloud. Falls back to nothing on desktop browsers that
  // don't support it; the copy/WhatsApp/text/email options below still work.
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  const handleNativeShare = async () => {
    if (!connectLink) return;
    try {
      await navigator.share({ title: "Connect with me on OkGather", text: SHARE_TEXT(connectLink), url: connectLink });
    } catch (err) {
      // Cancelled by the user, or unsupported - nothing to do.
    }
  };

  if (isVerifyingAuth || loading) {
    return <LoadingState />;
  }

  if (showLoginModal) {
    return (
      <div style={{ textAlign: "center", padding: "80px 20px" }}>
        <p style={{ color: colors.body, marginBottom: "16px", fontSize: "0.95rem" }}>Please log in to view your connections.</p>
        <Link to="/login" style={{ color: colors.primary, fontWeight: "700", fontSize: "0.95rem" }}>Log in</Link>
      </div>
    );
  }

  const incoming = connections.filter((c) => c.direction === "incoming" && c.status === "pending");
  const accepted = connections.filter((c) => c.status === "accepted");

  return (
    <>
      {/* Hero */}
      <div style={{ background: colors.gradientTint, borderBottom: `1px solid ${colors.primaryTintBorder}` }}>
        <div style={{ maxWidth: "640px", margin: "0 auto", padding: "48px 20px 36px", textAlign: "center" }}>
          <p style={type.eyebrow}>Your circle</p>
          <h1 style={{ ...type.h1, marginTop: "10px", marginBottom: "10px" }}>My Connections</h1>
          <p style={{ ...type.body, maxWidth: "440px", margin: "0 auto 24px" }}>
            The people you do things with — whether you met at a gathering or already knew each other.
            Not just event buddies: anyone you'd invite is welcome here.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "20px", flexWrap: "wrap", marginBottom: "26px" }}>
            <StatPill count={accepted.length} label={accepted.length === 1 ? "connection" : "connections"} />
            {incoming.length > 0 && <StatPill count={incoming.length} label="pending request" accent />}
          </div>
          <Link to="/circles" style={buttonPrimary} {...primaryHover}>
            Plan a hangout with your connections →
          </Link>
        </div>
      </div>

      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "36px 20px 64px" }}>
        {errorMessage && (
          <p style={{ color: colors.danger, textAlign: "center", marginBottom: "20px", fontSize: "0.9rem" }}>{errorMessage}</p>
        )}

        {/* Add an existing friend - the growth-critical action, given top billing */}
        <section style={{
          ...cardStyle,
          background: colors.gradientPrimary,
          border: "none",
          boxShadow: shadow.raised,
          marginBottom: "20px",
          color: "#fff",
        }}>
          <h2 style={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem", marginTop: 0, marginBottom: "14px" }}>
            🔗 Add an existing friend
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "18px" }}>
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <span style={{ fontSize: "1.1rem", lineHeight: 1.4 }} aria-hidden="true">🎉</span>
              <p style={{ color: "rgba(255,255,255,0.95)", fontSize: "0.88rem", lineHeight: 1.5, margin: 0 }}>
                <strong>In person:</strong> tap "Share in person" to beam your link to their phone,
                or show them your QR code to scan.
              </p>
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <span style={{ fontSize: "1.1rem", lineHeight: 1.4 }} aria-hidden="true">🏠</span>
              <p style={{ color: "rgba(255,255,255,0.95)", fontSize: "0.88rem", lineHeight: 1.5, margin: 0 }}>
                <strong>Remote:</strong> copy your link or send it straight via WhatsApp, text, or
                email.
              </p>
            </div>
          </div>
          <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.82rem", lineHeight: 1.5, marginBottom: "18px" }}>
            Either way, if they're not on OkGather yet they'll grab a free account first and land
            right back here, connected to you.
          </p>

          {linkLoading ? (
            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Loading your link...</p>
          ) : connectLink ? (
            <>
              {canNativeShare && (
                <button
                  onClick={handleNativeShare}
                  style={{
                    display: "block", width: "100%", boxSizing: "border-box",
                    backgroundColor: "#fff", color: colors.primaryDark, border: "none", borderRadius: radii.sm,
                    padding: "12px 18px", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", marginBottom: "10px",
                  }}
                >
                  📲 Share in person
                </button>
              )}
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                <input
                  type="text"
                  value={connectLink}
                  readOnly
                  onFocus={(e) => e.target.select()}
                  style={{
                    flex: "1 1 220px",
                    padding: "12px 14px",
                    borderRadius: radii.sm,
                    border: "none",
                    color: colors.body,
                    fontSize: "0.85rem",
                    backgroundColor: "#fff",
                  }}
                />
                <button
                  onClick={handleCopyLink}
                  style={{
                    backgroundColor: "rgba(255,255,255,0.16)", color: "#fff", border: "1px solid rgba(255,255,255,0.4)", borderRadius: radii.sm,
                    padding: "12px 18px", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {copySuccess ? "✓ Copied!" : "📋 Copy"}
                </button>
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <ShareChip label="WhatsApp" onClick={() => shareVia("whatsapp")} />
                <ShareChip label="Text" onClick={() => shareVia("sms")} />
                <ShareChip label="Email" onClick={() => shareVia("email")} />
                <ShareChip label={showQr ? "Hide QR code" : "📷 QR code"} onClick={() => setShowQr((v) => !v)} />
              </div>
              {showQr && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", marginTop: "16px" }}>
                  <QrCode value={connectLink} />
                  <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.8rem", textAlign: "center", margin: 0 }}>
                    Have them scan this with their phone camera to connect.
                  </p>
                </div>
              )}
            </>
          ) : (
            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.85rem" }}>Couldn't load your link right now.</p>
          )}
        </section>

        {/* Why connect */}
        <section style={{ ...cardStyle, marginBottom: "28px" }}>
          <h2 style={type.h2}>Why it's worth adding people</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px" }}>
            {BENEFITS.map((b) => (
              <div key={b.title} style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
                <div style={{
                  width: "38px", height: "38px", borderRadius: radii.sm, flexShrink: 0,
                  background: colors.primaryTint, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.15rem",
                }} aria-hidden="true">
                  {b.icon}
                </div>
                <div>
                  <p style={{ color: colors.ink, fontWeight: 700, fontSize: "0.92rem", margin: "0 0 2px" }}>{b.title}</p>
                  <p style={{ color: colors.muted, fontSize: "0.88rem", lineHeight: 1.55, margin: 0 }}>{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {incoming.length > 0 && (
          <section style={{ marginBottom: "28px" }}>
            <h2 style={{ ...type.h2, marginBottom: "12px" }}>Requests</h2>
            {incoming.map((c) => (
              <div key={c.id} style={personRowStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Avatar firstName={c.otherUser.firstName} lastName={c.otherUser.lastName} size={38} />
                  <span style={{ color: colors.ink, fontWeight: "600", fontSize: "0.95rem" }}>
                    {c.otherUser.firstName} {c.otherUser.lastName}
                  </span>
                </div>
                <button
                  onClick={() => handleAccept(c.id)}
                  disabled={acceptingId === c.id}
                  style={{
                    ...buttonPrimary,
                    padding: "8px 18px",
                    fontSize: "0.85rem",
                    boxShadow: "none",
                    cursor: acceptingId === c.id ? "default" : "pointer",
                    opacity: acceptingId === c.id ? 0.7 : 1,
                  }}
                >
                  {acceptingId === c.id ? "Accepting..." : "Accept"}
                </button>
              </div>
            ))}
          </section>
        )}

        <section>
          <h2 style={{ ...type.h2, marginBottom: "12px" }}>Connections</h2>
          {accepted.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: "center", padding: "32px 24px" }}>
              <p style={{ fontSize: "1.6rem", marginBottom: "10px" }} aria-hidden="true">👋</p>
              <p style={{ color: colors.ink, fontWeight: 700, fontSize: "0.98rem", marginBottom: "6px" }}>No connections yet</p>
              <p style={{ color: colors.muted, fontSize: "0.9rem", lineHeight: 1.55, maxWidth: "360px", margin: "0 auto" }}>
                Share your link above to add an existing friend, or add someone new after your next gathering.
              </p>
            </div>
          ) : (
            accepted.map((c) => (
              <div key={c.id} style={{ ...personRowStyle, justifyContent: "flex-start" }}>
                <Avatar firstName={c.otherUser.firstName} lastName={c.otherUser.lastName} size={38} />
                <span style={{ color: colors.ink, fontWeight: "600", fontSize: "0.95rem", marginLeft: "12px" }}>
                  {c.otherUser.firstName} {c.otherUser.lastName}
                </span>
              </div>
            ))
          )}
        </section>
      </div>
    </>
  );
};

const StatPill = ({ count, label, accent }) => (
  <div style={{
    display: "flex", alignItems: "baseline", gap: "6px",
    background: accent ? colors.warningTint : "#fff",
    border: `1px solid ${accent ? colors.warningBorder : colors.border}`,
    borderRadius: radii.pill, padding: "6px 16px",
  }}>
    <span style={{ fontWeight: 800, fontSize: "1.05rem", color: accent ? colors.warning : colors.ink }}>{count}</span>
    <span style={{ fontSize: "0.82rem", color: accent ? colors.warning : colors.muted, fontWeight: 600 }}>{label}</span>
  </div>
);

const ShareChip = ({ label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      backgroundColor: "rgba(255,255,255,0.16)",
      color: "#fff",
      border: "1px solid rgba(255,255,255,0.4)",
      borderRadius: radii.pill,
      padding: "7px 16px",
      fontWeight: 600,
      fontSize: "0.8rem",
      cursor: "pointer",
    }}
  >
    {label}
  </button>
);

const personRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  border: `1px solid ${colors.border}`,
  borderRadius: radii.md,
  padding: "12px 16px",
  marginBottom: "10px",
  background: colors.surface,
};

export default Connections;
